import { TabSection } from './TabSection'
import { Toggle } from '../Toggle'
import { Icon } from '../Icon'
import type { ChannelConfigUpdate, NotificationType, OpenNotificationTemplate } from '../../types'
import rowStyles from './ReviewUpdatesTab.module.css'
import noteStyles from './FiltersTab.module.css'
import styles from './CrosspostsTab.module.css'

const REACTION_ROW_TYPES: NotificationType[] = ['REVIEW_DECISION', 'REVIEW_COMMENT', 'MERGE_CLOSE']

const DEFAULT_REACTION_TYPES: NotificationType[] =
  ['REVIEW_REQUEST', 'REVIEW_DECISION', 'REVIEW_COMMENT', 'MERGE_CLOSE']

const REACTION_EMOJI: Partial<Record<NotificationType, string>> = {
  REVIEW_DECISION: '/emoji-approved.png',
  REVIEW_COMMENT:  '/emoji-comment.png',
  MERGE_CLOSE:     '/emoji-merged.png',
}

const ROW_META: Record<NotificationType, { label: string; hint: string }> = {
  REVIEW_REQUEST:  { label: 'PR state changes', hint: 'PR converted to draft, reopened, or marked ready for review' },
  REVIEW_DECISION: { label: 'Review decision',  hint: 'Someone approved or requested changes on the PR' },
  REVIEW_COMMENT:  { label: 'Review comment',   hint: 'Someone left comments with no approve/reject decision' },
  MERGE_CLOSE:     { label: 'Merge / close',    hint: 'PR was merged or closed' },
}

const MESSAGE_TEMPLATES: { value: OpenNotificationTemplate; name: string; desc: string }[] = [
  { value: 'LEAN',     name: 'Lean',     desc: 'Compact — title, author, and repo' },
  { value: 'STANDARD', name: 'Standard', desc: 'Full context — overview, diff stats, and CTA' },
]

function toggleInSet(current: NotificationType[], type: NotificationType, on: boolean): NotificationType[] {
  return on
    ? [...new Set([...current, type])]
    : current.filter(t => t !== type)
}

interface Props {
  isAdmin: boolean
  draft: {
    receiveCrosspostsEnabled?: boolean
    crosspostReactionNotifications?: NotificationType[]
    crosspostReplyNotifications?: NotificationType[]
    crosspostReviewerTaggingEnabled?: boolean
    crosspostMessageTemplate?: OpenNotificationTemplate
    enabledNotifications?: NotificationType[]
    reactionNotifications?: NotificationType[]
    replyNotifications?: NotificationType[]
    reviewerTaggingEnabled?: boolean
  }
  onChange: (patch: Partial<ChannelConfigUpdate>) => void
}

export function CrosspostsTab({ isAdmin, draft, onChange }: Props) {
  const receiveEnabled   = draft.receiveCrosspostsEnabled ?? true
  const reactionTypes    = draft.crosspostReactionNotifications ?? DEFAULT_REACTION_TYPES
  const replyTypes       = draft.crosspostReplyNotifications ?? []
  const reviewerTagging  = draft.crosspostReviewerTaggingEnabled ?? true
  const messageTemplate  = draft.crosspostMessageTemplate ?? 'LEAN'
  const stateChangeReplyOn = replyTypes.includes('REVIEW_REQUEST')

  function copyFromDirectSettings() {
    const enabled   = draft.enabledNotifications ?? []
    const reactions = draft.reactionNotifications ?? []
    const replies   = draft.replyNotifications ?? []
    onChange({
      crosspostReactionNotifications: enabled.filter(t => reactions.includes(t)),
      crosspostReplyNotifications: enabled.filter(t => replies.includes(t)),
      crosspostReviewerTaggingEnabled: draft.reviewerTaggingEnabled ?? false,
    })
  }

  return (
    <TabSection
      accent="teal"
      icon="call_split"
      title="Crossposts"
      subtitle="Settings for PRs posted here because this channel owns the repo — independent of your direct subscriptions"
    >
      {isAdmin && (
        <Toggle
          label="Receive crossposts"
          hint="Post PRs from other channels here when this channel owns the repo"
          checked={receiveEnabled}
          onChange={v => onChange({ receiveCrosspostsEnabled: v })}
        />
      )}

      {receiveEnabled && (
        <>
          <p className={styles.sectionIntro}>
            This channel gets crossposts because your team owns the repo and this channel is set as its
            home channel — in Backstage IDP, or as a configured crosspost target. Choose which updates you
            want to see for those posts.
          </p>

          <div className={rowStyles.templateSection}>
            <div className={rowStyles.sectionLabel}>Message format</div>
            <div className={rowStyles.templateOptions}>
              {MESSAGE_TEMPLATES.map(t => (
                <div
                  key={t.value}
                  className={`${rowStyles.templateOption} ${messageTemplate === t.value ? rowStyles.templateOptionActive : ''}`}
                  onClick={() => onChange({ crosspostMessageTemplate: t.value })}
                >
                  <div className={rowStyles.templateName}>{t.name}</div>
                  <div className={rowStyles.templateDesc}>{t.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={rowStyles.sectionDivider}>Notifications</div>
          <div className={rowStyles.notifList}>
            {REACTION_ROW_TYPES.map(type => {
              const { label, hint } = ROW_META[type]
              const emojiSrc    = REACTION_EMOJI[type]
              const reactionOn  = reactionTypes.includes(type)
              const replyOn     = replyTypes.includes(type)
              return (
                <div key={type} className={rowStyles.notifRow}>
                  <div className={styles.rowInfo}>
                    <div className={rowStyles.rowLabel}>{label}</div>
                    <div className={rowStyles.rowHint}>{hint}</div>
                  </div>
                  <div className={rowStyles.subOptions}>
                    <div
                      className={`${rowStyles.subOption} ${reactionOn ? rowStyles.subOptionOn : ''}`}
                      onClick={() => onChange({ crosspostReactionNotifications: toggleInSet(reactionTypes, type, !reactionOn) })}
                    >
                      {emojiSrc && <img src={emojiSrc} alt="" className={rowStyles.emojiIcon} />}
                      <span className={rowStyles.subLabel}>React</span>
                    </div>
                    <div
                      className={`${rowStyles.subOption} ${replyOn ? rowStyles.subOptionOn : ''}`}
                      onClick={() => onChange({ crosspostReplyNotifications: toggleInSet(replyTypes, type, !replyOn) })}
                    >
                      <span className={rowStyles.subLabel}>Thread update</span>
                    </div>
                  </div>
                </div>
              )
            })}

            <div className={rowStyles.notifRow}>
              <div className={styles.rowInfo}>
                <div className={rowStyles.rowLabel}>{ROW_META.REVIEW_REQUEST.label}</div>
                <div className={rowStyles.rowHint}>{ROW_META.REVIEW_REQUEST.hint}</div>
              </div>
              <div className={rowStyles.subOptions}>
                <div
                  className={`${rowStyles.subOption} ${stateChangeReplyOn ? rowStyles.subOptionOn : ''}`}
                  onClick={() => onChange({
                    crosspostReplyNotifications: toggleInSet(replyTypes, 'REVIEW_REQUEST', !stateChangeReplyOn)
                  })}
                >
                  <span className={rowStyles.subLabel}>Thread update</span>
                </div>
              </div>
            </div>
          </div>

          <Toggle
            label="Tag reviewers on crossposts"
            hint='cc: @reviewer on the crosspost, plus the "a review has been requested from you" thread reply'
            checked={reviewerTagging}
            onChange={v => onChange({ crosspostReviewerTaggingEnabled: v })}
            noBorder
          />

          <div className={styles.copyRow}>
            <button type="button" className={styles.copyBtn} onClick={copyFromDirectSettings}>
              Copy from my PR settings
            </button>
            <span className={styles.copyHint}>
              Fills in reactions, thread updates, and reviewer tagging from this channel's direct PR settings — not the message format
            </span>
          </div>
        </>
      )}

      <div className={noteStyles.notes}>
        <div className={noteStyles.note}>
          <Icon name="info" size="sm" className={noteStyles.noteIcon} />
          A crossposted PR gets reactions and reviewer tags but no thread replies by default.
        </div>
        <div className={noteStyles.note}>
          <Icon name="filter_alt" size="sm" className={noteStyles.noteIcon} />
          Turning off "Receive crossposts" only stops new ones — pause and the repo, label, and author filters also block crossposts.
        </div>
        <div className={noteStyles.note}>
          <Icon name="history" size="sm" className={noteStyles.noteIcon} />
          Crossposts started before this channel had its own crosspost settings keep following the direct PR settings until they close.
        </div>
      </div>
    </TabSection>
  )
}
